import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { SHADCN, R, TYPO, S, FS } from '../theme';

// ── <Stat> — KPI tile shadcn dark ───────────────────────────────────────────
// Pattern : titre 14/600 + metric 36/600 + delta (flèche + %) + sparkline.

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function Sparkline({ data = [], color = SHADCN.success, width = 96, height = 40 }) {
  const lineRef = useRef(null);
  const [len, setLen] = useState(null);

  useEffect(() => {
    if (!lineRef.current) return;
    const total = lineRef.current.getTotalLength();
    setLen(total);
  }, [data]);

  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data.map((v, i) => [i * stepX, height - ((v - min) / range) * height]);
  const pathLine = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const pathArea = `${pathLine} L${width},${height} L0,${height} Z`;
  const gradId = `spark-grad-${Math.random().toString(36).slice(2, 8)}`;
  const reduced = prefersReducedMotion();
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={pathArea}
        fill={`url(#${gradId})`}
        style={{
          opacity: reduced || len == null ? 1 : 0,
          animation: reduced || len == null ? undefined : 'sk-spark-fill 700ms cubic-bezier(0.22, 1, 0.36, 1) 250ms forwards',
        }}
      />
      <path
        ref={lineRef}
        d={pathLine}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={
          reduced || len == null
            ? undefined
            : {
                strokeDasharray: len,
                strokeDashoffset: len,
                animation: 'sk-spark-draw 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards',
              }
        }
      />
    </svg>
  );
}

const TONES = {
  info:    { bg: SHADCN.cardSolid,                     border: SHADCN.border,                             color: SHADCN.fg,       labelColor: SHADCN.mutedFg, subColor: SHADCN.mutedFg },
  success: { bg: SHADCN.accentSoft,                   border: SHADCN.accentBorder,                       color: SHADCN.success,  labelColor: SHADCN.success, subColor: SHADCN.success },
  warning: { bg: 'oklch(0.852 0.199 91.936 / 0.08)',  border: 'oklch(0.852 0.199 91.936 / 0.3)',         color: SHADCN.warning,  labelColor: SHADCN.warning, subColor: SHADCN.warning },
  error:   { bg: 'oklch(0.577 0.245 27.325 / 0.12)',  border: 'oklch(0.577 0.245 27.325 / 0.4)',         color: SHADCN.error,    labelColor: SHADCN.error,   subColor: SHADCN.error },
  accent:  { bg: SHADCN.accentSoft,                   border: SHADCN.accentBorder,                       color: SHADCN.accent,   labelColor: SHADCN.accent,  subColor: SHADCN.accent },
};

function resolveTone(name) {
  if (name === 'danger') return TONES.error;
  return TONES[name] || TONES.info;
}

export default function Stat({
  label,
  value,
  sub,
  delta,
  spark,
  tone = 'info',
  rightSlot,
  style,
  progress,
}) {
  const t = resolveTone(tone);
  const deltaUp = delta?.direction === 'up';
  const deltaColor = delta ? (deltaUp ? SHADCN.success : SHADCN.error) : SHADCN.mutedFg;
  const sparkColor = delta ? (deltaUp ? SHADCN.success : SHADCN.error) : SHADCN.primary;
  const progressPct = progress && progress.max > 0
    ? Math.max(0, Math.min(1, progress.value / progress.max)) * 100
    : 0;
  const progressColor = progress?.color || t.color;

  // ── Milestone crossing — célèbre une fois quand progress passe sous 100 → ≥100
  const wasReached = useRef(progressPct >= 100);
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    if (!progress || progress.max <= 0) return;
    const reached = progressPct >= 100;
    if (reached && !wasReached.current && !prefersReducedMotion()) {
      setCelebrate(true);
      const id = setTimeout(() => setCelebrate(false), 1400);
      wasReached.current = true;
      return () => clearTimeout(id);
    }
    if (!reached) wasReached.current = false;
  }, [progressPct, progress]);

  // Delta affiché en pill top-right (style "Midnight Mint")
  const deltaPillBg     = delta ? (deltaUp ? SHADCN.accentSoft   : 'oklch(0.577 0.245 27.325 / 0.12)') : 'transparent';
  const deltaPillBorder = delta ? (deltaUp ? SHADCN.accentBorder : 'oklch(0.577 0.245 27.325 / 0.4)')  : 'transparent';
  const deltaPill = delta ? (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      padding: `3px ${S[2]}px`,
      borderRadius: R.pill,
      background: deltaPillBg,
      border: `1px solid ${deltaPillBorder}`,
      color: deltaColor,
      fontSize: FS.caption,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
      lineHeight: 1,
      whiteSpace: 'nowrap',
    }}>
      {deltaUp ? <ArrowUp size={11} strokeWidth={2.6} /> : <ArrowDown size={11} strokeWidth={2.6} />}
      {delta.value}%
    </span>
  ) : null;

  const bottomText = delta?.suffix || sub;

  return (
    <div style={{
      flex: 1, minWidth: 220,
      padding: S[5],
      border: `1px solid ${t.border}`,
      borderRadius: R.xl,
      background: t.bg,
      color: t.color,
      transition: 'border-color 600ms cubic-bezier(0.22, 1, 0.36, 1), background 600ms cubic-bezier(0.22, 1, 0.36, 1)',
      animation: celebrate ? 'sk-milestone 1200ms cubic-bezier(0.22, 1, 0.36, 1)' : undefined,
      ...style,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: S[3], marginBottom: S[3] }}>
        <div style={{ fontSize: 13, fontWeight: 400, color: t.labelColor }}>{label}</div>
        {rightSlot ?? deltaPill}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: S[4] }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 40, fontWeight: 600, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1, color: t.color }}>{value}</div>
          {bottomText && (
            <div style={{ marginTop: S[3], ...TYPO.body, fontWeight: 400, color: t.subColor }}>
              {bottomText}
            </div>
          )}
        </div>
        {spark && spark.length > 1 && <Sparkline data={spark} color={sparkColor} />}
      </div>
      {progress && progress.max > 0 && (
        <div style={{ marginTop: S[3] }}>
          <div style={{ height: 4, background: SHADCN.border, borderRadius: R.pill, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: '100%',
              transform: `scaleX(${progressPct / 100})`,
              transformOrigin: 'left',
              background: progressColor,
              borderRadius: R.pill,
              transition: 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
            }} />
          </div>
        </div>
      )}
    </div>
  );
}
